import type { TFunction } from "i18next";
import { ApiError, shouldSuppressLocalApiErrorToast } from "@/lib/api-client";
import { isPollBudgetElapsedError } from "@/lib/poll-budget-elapsed";
import {
  AmberWorkerFailureError,
  resolveAmberErrorToastMessage,
} from "@/lib/use-amber-mutation";

export type PostFlightDoubleDownPostFailureKind =
  | "stale-list"
  | "ad-gone"
  | "image-blocked"
  | "type-disabled"
  | "inline"
  | "toast";

export function classifyPostFlightDoubleDownPostError(
  error: unknown,
): PostFlightDoubleDownPostFailureKind {
  if (!(error instanceof ApiError)) return "toast";
  if (error.status === 409 && error.code === "POST_FLIGHT_LIST_REFRESH_REQUIRED") {
    return "stale-list";
  }
  if (error.code === "POST_FLIGHT_PARENT_NOT_FOUND") return "ad-gone";
  if (error.code === "POST_FLIGHT_IMAGE_AD_BLOCKED") return "image-blocked";
  if (error.code === "OPPORTUNITY_TYPE_DISABLED") return "type-disabled";
  if (error.status === 400) return "inline";
  return "toast";
}

export function isPostFlightDoubleDownAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function isPostFlightDoubleDownToastFailureKind(kind: PostFlightDoubleDownPostFailureKind): boolean {
  return kind === "toast" || kind === "type-disabled";
}

export function shouldSuppressPostFlightDoubleDownErrorToast(error: unknown): boolean {
  if (isPollBudgetElapsedError(error)) return true;
  if (isPostFlightDoubleDownAbortError(error)) return true;
  const kind = classifyPostFlightDoubleDownPostError(error);
  return !isPostFlightDoubleDownToastFailureKind(kind);
}

/** Toast copy for failures that are not shown inline in the double-down dialog. */
export function resolvePostFlightDoubleDownErrorToastMessage(
  error: unknown,
  t: TFunction,
): string | null {
  if (isPollBudgetElapsedError(error) || isPostFlightDoubleDownAbortError(error)) return null;
  if (shouldSuppressLocalApiErrorToast(error)) return null;

  if (error instanceof AmberWorkerFailureError) {
    return (
      resolveAmberErrorToastMessage(error, { passThroughWorkerFailure: true }) ??
      postFlightDoubleDownFailureMessage(
        t,
        "toast",
        new ApiError(400, error.message, error.code),
      )
    );
  }

  if (!(error instanceof ApiError)) {
    return postFlightDoubleDownFailureMessage(
      t,
      "toast",
      new ApiError(0, "Network error", "NETWORK_ERROR"),
    );
  }

  const kind = classifyPostFlightDoubleDownPostError(error);
  if (!isPostFlightDoubleDownToastFailureKind(kind)) return null;
  return postFlightDoubleDownFailureMessage(t, kind, error);
}

export function postFlightDoubleDownFailureMessage(
  t: TFunction,
  kind: PostFlightDoubleDownPostFailureKind,
  error: ApiError,
): string {
  switch (kind) {
    case "stale-list":
      return t(
        "postFlight.doubleDown.error.staleList",
        "The opportunity list is too old to generate from. Reload the list, then try again.",
      );
    case "image-blocked":
      return t("postFlight.doubleDown.imageBlocked", "Double Down isn't available for image ads.");
    case "type-disabled":
      return t(
        "postFlight.doubleDown.error.typeDisabled",
        "Post-Flight generation is turned off for your organization. Refresh the page or ask an admin to re-enable it.",
      );
    case "ad-gone":
      return t(
        "postFlight.doubleDown.error.adGone",
        "This ad is no longer in your Post-Flight results.",
      );
    case "inline":
      return error.message.trim().length > 0
        ? error.message
        : t(
            "postFlight.doubleDown.error.genericPost",
            "Couldn't start generating scripts. Try again.",
          );
    default:
      return t(
        "postFlight.doubleDown.error.genericPost",
        "Couldn't start generating scripts. Try again.",
      );
  }
}
